import { lookParId } from './looks';
import { bubbleSVG } from './constants';
import { arreterLeTempsFort, demarrerLeTempsFort } from './tempsFort';
import { BLANC, couleurSecondaire } from 'shared/helpers/colorHelper';

/**
 * LE LANCEUR COMPOSE : LE MONTAGE, L'ETAT, LA FEUILLE.
 *
 * CE FICHIER NE DESSINE RIEN. Chaque look apporte sa feuille et son montage ; ici vit ce qui leur
 * est commun : ou le conteneur se pose, quels jetons de theme il porte, quel etat il montre, et
 * quand la preference « moins de mouvement » les arrete.
 *
 * `aucun` N'EST PAS UN LOOK PAR DEFAUT, C'EST L'ABSENCE DE LOOK. Rien n'est monte, rien n'est
 * injecte, aucune classe ne change : la bulle d'aujourd'hui est exactement celle d'hier. C'est ce
 * que recoit toute inbox restee en v1, puisque le serveur y force le mot.
 */
let conteneur = null;
let monte = null;

export const lanceurActif = () => conteneur;

// LE REGLAGE DU CLIENT : « Adapter la couleur secondaire », active sauf refus explicite.
export const couleurSecondaireAdaptee = lanceur =>
  !lanceur || lanceur.adapt_secondary_color !== false;

/**
 * UNE COULEUR PALE NE PORTE PAS DE DESSIN BLANC, ET NE S'ECRIT PAS SUR UNE PAGE BLANCHE.
 *
 * La couleur secondaire, ce qui se pose sur la couleur, vient d'une seule regle
 * (`couleurSecondaire`) : la bulle classique, les croix, les etiquettes et les dessins des looks
 * la relisent tous, avec le reglage du client. Deux regles differentes pour la meme question
 * donneraient deux reponses.
 *
 * DEUX JETONS PARCE QU'IL Y A DEUX FONDS. Une croix posee SUR LA COULEUR (Phare, Orbe, Aurore,
 * Braise) veut le contraire de la couleur ; une croix tracee SUR LA PAGE ou sur le papier du
 * theme (Trait, Vitre, Iris, Bouche) veut la couleur elle-meme, tant qu'elle se lit — et l'encre
 * du theme, qui suit le clair et le sombre, quand elle ne se lit plus. Une couleur normale ne
 * change donc rien : les deux jetons rendent exactement ce que la planche a valide.
 */
export const jetonsDeCroix = (widgetColor, adaptee = true) => {
  const secondaire = couleurSecondaire(widgetColor, adaptee);
  return {
    '--woot-croix-auto': secondaire,
    '--woot-encre-auto':
      secondaire === BLANC ? 'var(--client)' : 'var(--woot-encre)',
  };
};

/**
 * LES MEMES REPONSES, EN COULEURS ECRITES, POUR UN DESSIN POSE EN IMAGE.
 *
 * Une image ne lit pas les variables de la page : le look `mesure` ecrit ces trois couleurs dans
 * le dessin qui les nomme. La couleur elle-meme ; ce qui se pose dessus, comme la croix ; la
 * couleur prise comme encre sur du blanc, comme le trait. Le blanc d'un dessin est le sien, il ne
 * suit pas le theme de la page : son encre sombre est le fonce de la teinte, jamais celle du theme.
 */
export const couleursDuDessin = (widgetColor, adaptee = true) => {
  const secondaire = couleurSecondaire(widgetColor, adaptee);
  return {
    widget: widgetColor,
    surWidget: secondaire,
    encre: secondaire === BLANC ? widgetColor : secondaire,
  };
};

const sansMouvement = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * LA FEUILLE DU LOOK VIT DANS SON PROPRE ELEMENT, restaure apres un Turbo comme celle du SDK.
 * Une seule est injectee par page : un look par inbox, et le SDK ne monte qu'un lanceur.
 */
const injecterLaFeuille = look => {
  if (document.getElementById('cw-look-styles')) return;
  const feuille = document.createElement('style');
  feuille.id = 'cw-look-styles';
  feuille.dataset.turboPermanent = true;
  feuille.innerHTML = look.css;
  document.body.appendChild(feuille);
};

/**
 * CE QUE LA FEUILLE N'ATTEINT PAS. Une animation SMIL vit dans le SVG, pas dans le CSS : elle
 * ignore `animation: none !important` et continue de tourner chez quelqu'un qui a demande l'arret.
 * Le seul levier est `pauseAnimations()`, sur le SVG lui-meme. Le balayage est generique — un SVG
 * qui porte une balise d'animation — parce que le modele ne veut pas savoir quel look dessine
 * quoi ; ce que seul le look sait, il le fait dans son propre geste.
 */
const arreterLeMouvement = (racine, dessin) => {
  racine.querySelectorAll('svg').forEach(svg => {
    if (!svg.querySelector('animate, animateMotion, animateTransform')) return;
    if (typeof svg.pauseAnimations === 'function') svg.pauseAnimations();
  });
  if (dessin.gestes && typeof dessin.gestes.reduireLeMouvement === 'function') {
    dessin.gestes.reduireLeMouvement(racine);
  }
};

/**
 * CE QUE LE SDK SAIT DESSINER, ET QUI SE DEMANDE AVANT DE DESHABILLER LA BULLE.
 *
 * Deshabiller le bouton sur le MOT plutot que sur le dessin donnait un carre de 64 px vide : fond
 * transparent, ombre retiree, aucune icone posee, et rien monte par-dessus puisque le registre ne
 * connaissait pas le mot. C'est le chemin qu'ouvre le premier look ajoute cote serveur pour tout
 * visiteur dont le SDK en cache est plus ancien. La question se pose donc ici, avant.
 */
export const lookDessinable = look =>
  Boolean(look && look !== 'aucun' && lookParId(look));

/**
 * Monte le lanceur d'un look dans le holder de la bulle. Rend le conteneur, ou `null` quand il
 * n'y a rien a monter — et dans ce cas l'appelant garde la bulle d'aujourd'hui, telle quelle.
 *
 * `lanceur` est ce que le serveur a servi (`launcher_pour_le_sdk`) : seul le dessin sur mesure en
 * lit quelque chose, ses images. Les neuf autres l'ignorent, comme `couleurs`, que lui seul ecrit
 * dans ses images.
 */
export const monterLeLanceur = ({
  look,
  widgetColor,
  position,
  parent,
  lanceur,
}) => {
  if (!look || look === 'aucun' || !parent) return null;

  const dessin = lookParId(look);
  if (!dessin) {
    // PAS DE REPLI SILENCIEUX. Un mot que le serveur accepte et que le SDK ne sait pas encore
    // dessiner est une incoherence de version, pas un choix du client : elle se dit.
    // eslint-disable-next-line no-console
    console.warn(
      `[widget] look inconnu du SDK, la bulle par defaut est rendue : ${look}`
    );
    return null;
  }

  conteneur = document.createElement('div');
  conteneur.className = `woot-lanceur woot-elements--${position}`;
  conteneur.dataset.look = dessin.id;
  conteneur.dataset.etat = 'repos';
  // La couleur de l'inbox est le seul jeton qui vienne du client ; les looks en derivent leurs
  // teintes. Les autres jetons suivent le theme du visiteur, donc ils vivent dans la feuille.
  if (widgetColor) conteneur.style.setProperty('--client', widgetColor);
  const adaptee = couleurSecondaireAdaptee(lanceur);
  Object.entries(jetonsDeCroix(widgetColor, adaptee)).forEach(
    ([jeton, valeur]) => conteneur.style.setProperty(jeton, valeur)
  );

  injecterLaFeuille(dessin);
  dessin.monter(conteneur, {
    position,
    lanceur,
    couleurs: couleursDuDessin(widgetColor, adaptee),
  });
  parent.appendChild(conteneur);

  monte = dessin;
  if (sansMouvement()) arreterLeMouvement(conteneur, dessin);
  demarrerLeTempsFort({ conteneur, gestes: dessin.gestes });

  return conteneur;
};

/**
 * LA BASCULE. Rend `true` quand un look l'a prise en charge : dans ce cas les deux bulles ne
 * s'echangent plus, le lanceur reste en place et passe a l'etat ouvert.
 */
export const basculerLeLanceur = ouvert => {
  if (!conteneur) return false;

  if (ouvert) {
    // `attention` D'ABORD EFFACE. Le visiteur qui ouvre pendant un temps fort verrait sinon la
    // fin d'un mouvement d'appel par-dessus la croix de fermeture.
    if (conteneur.dataset.etat === 'attention')
      conteneur.dataset.etat = 'repos';
    conteneur.dataset.etat = 'ouvert';
    arreterLeTempsFort();
  } else {
    conteneur.dataset.etat = 'repos';
  }

  if (monte && monte.gestes) {
    const geste = ouvert ? monte.gestes.ouvrir : monte.gestes.fermer;
    if (typeof geste === 'function') geste(conteneur);
  }

  return true;
};

// ---------------------------------------------------------------------------
// LA VITRINE : MONTRER LES NEUF, SANS EN SERVIR AUCUN.
//
// L'ecran de reglages doit montrer les neuf dessins en meme temps, pour qu'on choisisse en voyant
// plutot que sur une phrase. `monterLeLanceur` ne le peut pas, et ce n'est pas un oubli : il tient
// l'etat du lanceur UNIQUE de la page — le conteneur monte, le temps fort qui le vise, la feuille
// unique. Neuf appels ecraseraient huit fois cet etat.
//
// Ici on monte donc sans rien memoriser : pas de singleton, pas de temps fort, pas de bascule.
// LE DESSIN, LUI, EST LE MEME, jeton pour jeton : une vitrine qui dessinerait autrement ne
// montrerait pas ce que le visiteur verra, et c'est tout ce qu'on lui demande.
// ---------------------------------------------------------------------------

// LE SOCLE D'UNE TUILE. Dans la page du client, `.woot-lanceur` est en position fixe dans un coin
// et la feuille du SDK le pose ; le tableau de bord ne charge pas cette feuille. Les jetons de
// theme sont donc repris ici a l'identique — `sdk.js` porte les memes, et une garde verifie qu'ils
// ne derivent pas. Seule la mise en place change : dans le flux, a l'echelle de la tuile.
export const CSS_VITRINE = `
.woot-vitrine {
  --woot-surface: #ffffff;
  --woot-filet: #dce1e8;
  --woot-encre: #101317;
  --woot-ombre: 0 10px 30px rgba(16, 19, 23, .10);
  position: relative;
  width: 76px;
  height: 88px;
  display: block;
  pointer-events: none;
  /* LA TUILE COUPE, LE COIN D'ECRAN NON, ET C'EST LA SEULE DIFFERENCE ASSUMEE. Trois looks
     debordent volontairement de leur disque : le faisceau de Phare balaie 260 px, la fenetre de
     Vitre fait 84 px de haut, les halos d'Aurore et de Braise passent le bord. Dans un coin de
     page c'est l'effet ; dans une grille, ca bave sur la tuile voisine. La boite est donc plus
     large que le lanceur, pour montrer le champ proche de chaque effet, et elle coupe au bord. */
  overflow: hidden;
}

/* LES MEMES JETONS QU'EN SOMBRE DANS LA PAGE DU CLIENT. Sans ce bloc, une tuile gardait le papier
   blanc de Trait et le verre clair de Vitre sur un tableau de bord sombre : la grille montrait un
   theme que le visiteur ne verra jamais. Une garde verifie que les deux jeux ne derivent pas. */
@media (prefers-color-scheme: dark) {
  .woot-vitrine {
    --woot-surface: #1b2028;
    --woot-filet: #262d37;
    --woot-encre: #eceff3;
    --woot-ombre: 0 10px 30px rgba(0, 0, 0, .5);
  }
}

.woot-vitrine .woot-lanceur {
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  bottom: auto;
  width: 64px;
  height: 64px;
  z-index: auto;
  cursor: inherit;
}

/* Vitre est plus haute que les autres, par dessin : on la recentre plutot que de la rogner en
   haut, sinon on montrerait une fenetre sans son toit. */
.woot-vitrine .woot-lanceur[data-look="vitre"] { height: 84px; }

/* LE CARTOUCHE NE SORT PAS EN VITRINE : il porte le texte du client, pas le dessin, et il
   deborderait de la tuile. On montre le lanceur, pas la phrase. */
.woot-vitrine .woot-lanceur__cartouche { display: none; }

/* LE DESSIN SUR MESURE EN VITRINE : le dessin seul, a l'echelle de la tuile. Dans la page du client
   il s'ancre dans son coin et porte son etiquette ; ici il n'a ni l'un ni l'autre. Le selecteur pese
   plus que celui du look, injecte apres ce socle. */
.woot-vitrine .woot-lanceur[data-look="mesure"].woot-elements--right {
  display: block;
  right: auto;
  bottom: auto;
  width: 64px;
  height: 64px;
}

.woot-vitrine .woot-lanceur[data-look="mesure"] .woot-mesure__badge { width: 64px; height: 64px; }

/* LE MOT « aucun » A UN RENDU, LUI AUSSI, ET C'EST LA BULLE D'AUJOURD'HUI. La premiere version laissait sa
   case vide : un trou dans la grille ne dit rien, decale la premiere rangee, et laisse croire que
   l'option n'a pas d'apparence. Or c'est la seule qu'un client connait deja. */
.woot-vitrine__bulle {
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 64px;
  height: 64px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .16);
}

.woot-vitrine__bulle svg { width: 24px; height: 24px; }
`;

const feuilleDeVitrine = look => {
  const id = `cw-vitrine-${look.id}`;
  if (document.getElementById(id)) return;
  const feuille = document.createElement('style');
  feuille.id = id;
  // LES NEUF FEUILLES COHABITENT, une par element. Chacune est portee par `[data-look="..."]`,
  // donc elles ne se marchent pas dessus ; il faut seulement les accumuler au lieu de s'arreter a
  // la premiere, comme le fait la feuille du lanceur servi, qui n'en monte jamais qu'un.
  feuille.innerHTML = look.css;
  document.head.appendChild(feuille);
};

// LA BULLE D'AUJOURD'HUI, AVEC LE MEME TRACE QUE CELUI QUE LE SDK POSE DANS LA PAGE DU CLIENT.
// Le chemin vient de `bubbleHelpers`, source unique : une vitrine qui redessinerait l'icone
// montrerait autre chose que la bulle reelle, et deriverait au premier ajustement.
const bulleDeVitrine = (widgetColor, parent, adaptee) => {
  const bulle = document.createElement('div');
  bulle.className = 'woot-vitrine__bulle';
  if (widgetColor) bulle.style.background = widgetColor;

  const icone = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icone.setAttribute('viewBox', '0 0 240 240');
  icone.setAttribute('fill', 'none');
  const trace = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  trace.setAttribute('d', bubbleSVG);
  trace.setAttribute('fill', couleurSecondaire(widgetColor, adaptee));
  icone.appendChild(trace);

  bulle.appendChild(icone);
  parent.appendChild(bulle);
  return bulle;
};

/**
 * Monte un look dans `parent` pour le montrer. Rend le conteneur, ou `null` si le mot est inconnu
 * ou si `aucun` : dans les deux cas l'appelant montre ce qu'il montrait deja.
 */
export const monterUneVitrine = ({
  look,
  widgetColor,
  parent,
  lanceur,
  adaptee = true,
}) => {
  if (!look || !parent) return null;

  const dessin = look === 'aucun' ? null : lookParId(look);
  if (look !== 'aucun' && !dessin) return null;

  if (!document.getElementById('cw-vitrine-socle')) {
    const socle = document.createElement('style');
    socle.id = 'cw-vitrine-socle';
    socle.innerHTML = CSS_VITRINE;
    document.head.appendChild(socle);
  }
  // `aucun` N'A PAS DE FEUILLE A INJECTER : sa bulle vit dans le socle, comme dans la page du
  // client ou elle vient de la feuille du SDK et non d'un look.
  if (dessin) feuilleDeVitrine(dessin);
  else return bulleDeVitrine(widgetColor, parent, adaptee);

  const vitrine = document.createElement('div');
  vitrine.className = 'woot-lanceur woot-elements--right';
  vitrine.dataset.look = dessin.id;
  vitrine.dataset.etat = 'repos';
  if (widgetColor) vitrine.style.setProperty('--client', widgetColor);
  Object.entries(jetonsDeCroix(widgetColor, adaptee)).forEach(
    ([jeton, valeur]) => vitrine.style.setProperty(jeton, valeur)
  );

  dessin.monter(vitrine, {
    position: 'right',
    lanceur,
    couleurs: couleursDuDessin(widgetColor, adaptee),
  });
  parent.appendChild(vitrine);
  if (sansMouvement()) arreterLeMouvement(vitrine, dessin);
  return vitrine;
};

const GESTE_PAR_ETAT = {
  attention: 'attention',
  ouvert: 'ouvrir',
  repos: 'fermer',
};

/**
 * Pose un etat sur une vitrine et joue le geste qui va avec, comme le fait le lanceur servi. Sans
 * ca, les looks dont l'appel est en JavaScript resteraient immobiles pendant que les autres
 * s'animent, et la grille mentirait sur trois d'entre eux.
 */
export const etatDeLaVitrine = (vitrine, etat) => {
  if (!vitrine || sansMouvement()) return;

  const dessin = lookParId(vitrine.dataset.look);
  vitrine.dataset.etat = etat;
  const geste = dessin && dessin.gestes && dessin.gestes[GESTE_PAR_ETAT[etat]];
  if (typeof geste === 'function') geste(vitrine);
};

/**
 * LE FOCUS CLAVIER SE VOIT SUR LE DESSIN. Avec un look, le bouton qui recoit le focus est
 * transparent et la composition le couvre : son contour ne se voyait plus. Le conteneur porte donc
 * le focus du bouton, au clavier seulement, et la feuille commune le dessine.
 */
// LE DERNIER GESTE SUR LA PAGE TRANCHE. A la fermeture, le SDK rend le focus au bouton, pour qu'un
// visiteur au clavier ou au lecteur d'ecran retrouve sa place. Chrome marque ce focus rendu par le
// code comme venu du clavier meme apres un clic, puisqu'il revient du panneau : l'anneau s'allumait
// a chaque fermeture a la souris (mesure). Un clic ou un toucher sur la page l'eteint donc jusqu'a
// la prochaine touche. Limite connue : une touche pressee dans le panneau ne se voit pas d'ici.
let dernierGeste = null;
let gestesSuivis = false;

const suivreLesGestes = () => {
  if (gestesSuivis) return;
  gestesSuivis = true;
  ['pointerdown', 'mousedown', 'touchstart'].forEach(type =>
    document.addEventListener(
      type,
      () => {
        dernierGeste = 'pointeur';
      },
      { capture: true, passive: true }
    )
  );
  document.addEventListener(
    'keydown',
    () => {
      dernierGeste = 'clavier';
    },
    true
  );
};

// Un navigateur qui ne connait pas :focus-visible leve sur le selecteur : le contour se montre alors
// a tout focus, ce qui vaut mieux que jamais.
const auClavier = bouton => {
  if (dernierGeste === 'pointeur') return false;
  try {
    return bouton.matches(':focus-visible');
  } catch (erreur) {
    return true;
  }
};

export const relierLeFocus = (bouton, lanceur) => {
  suivreLesGestes();
  bouton.addEventListener('focus', () => {
    if (auClavier(bouton)) lanceur.dataset.focus = 'oui';
  });
  bouton.addEventListener('blur', () => {
    delete lanceur.dataset.focus;
  });
};

export const demonterLeLanceur = () => {
  if (conteneur && conteneur.parentNode)
    conteneur.parentNode.removeChild(conteneur);
  const feuille = document.getElementById('cw-look-styles');
  if (feuille && feuille.parentNode) feuille.parentNode.removeChild(feuille);
  conteneur = null;
  monte = null;
};
